import { AfterViewInit, Component, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AnyCredentialRedemptionRequestProviderParams, isHidCredentialRedemptionResponseProviderParams, isLegicCredentialRedemptionResponseProviderParams, isWavelynxCredentialRedemptionResponseProviderParams } from '~api/helpers/types';
import { ErrorModel } from '~api/models/error.model';
import { HidCredentialRedemptionRequestProviderParamsModel } from '~api/models/hid-credential-redemption-request-provider-params.model';
import { LegicCredentialRedemptionRequestProviderParamsModel } from '~api/models/legic-credential-redemption-request-provider-params.model';
import { CredentialService } from '~api/services/credential.service';
import { Redirect } from '~app/provisioning/redirect/redirect';
import { ConfigService } from '~app/services/config.service';
import { CredentialInfo } from '~app/types/credential-info';
import { RedemptionInfo } from '~app/types/redemption-info';
import { CredentialRedemptionType } from '~generated-api/models/credential-redemption-type';

@Component({
  selector: 'app-platform-android',
  imports: [
    TranslateModule,
    Redirect
  ],
  templateUrl: './android.html',
  styleUrl: './android.css'
})
export class Android implements AfterViewInit {
  readonly redemptionInfo = input.required<RedemptionInfo>();
  readonly credentialInfo = input.required<CredentialInfo>();
  readonly providerError = output<string>();
  protected redirectTo: string | undefined = undefined;
  private readonly config = inject(ConfigService);
  private readonly credential = inject(CredentialService);

  ngAfterViewInit(): void {
    if (this.credentialInfo().provider === 'WAVELYNX') {
      this.credential.confirmCredentialRedemption(this.redemptionInfo().id!, CredentialRedemptionType.WALLET_ANDROID).subscribe(result => {
        if (result instanceof ErrorModel) {
          this.providerError.emit('cant get weblink');
        } else {
          try {
            const deepLink = isWavelynxCredentialRedemptionResponseProviderParams(result.providerParams) ? result.providerParams.webLink : null;
            if (deepLink) {
              this.redirectTo = deepLink;
              return;
            }
          } catch (e) {
          }
          this.providerError.emit('cant get/parse weblink');
        }
      });
    }
  }

  onStartProvision() {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: this.config.GOOGLE_CLIENT_ID!,
      scope: 'openid',
      callback: async (tokenResponse) => {
        const response = await fetch('https://openidconnect.googleapis.com/v1/userinfo', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${tokenResponse.access_token}`
          }
        });

        const data = await response.json();
        const nonce = uuidv4();
        const rawParams = {
          googleAud: this.config.GOOGLE_CLIENT_ID!,
          googleNonce: nonce,
          googleUser: (await this.generateSHA256(data.sub + nonce)).toLowerCase(),
        };
        let params: AnyCredentialRedemptionRequestProviderParams = undefined;
        if (this.credentialInfo().provider === 'LEGIC_CONNECT') {
          params = new LegicCredentialRedemptionRequestProviderParamsModel(rawParams);
        } else if (this.credentialInfo().provider === 'HID') {
          params = new HidCredentialRedemptionRequestProviderParamsModel(rawParams);
        }

        const result = await firstValueFrom(this.credential.confirmCredentialRedemption(this.redemptionInfo().id!, CredentialRedemptionType.WALLET_ANDROID, params));
        const providerParams = (result && 'providerParams' in result) ? result['providerParams'] : undefined;
        if (!providerParams) {
          this.providerError.emit('no providerParams in confirmCredentialRedemption() response');
          return null;
        }
        let deepLink = undefined;
        if (this.credentialInfo().provider === 'LEGIC_CONNECT' && isLegicCredentialRedemptionResponseProviderParams(providerParams)) {
          deepLink = providerParams.googleDeepLink;
        } else if (this.credentialInfo().provider === 'HID' && isHidCredentialRedemptionResponseProviderParams(providerParams)) {
          deepLink = providerParams.googleDeepLink;
        }
        if (!deepLink) {
          this.providerError.emit('no deepLink in confirmCredentialRedemption() response');
          return null;
        }
        this.redirectTo = deepLink;
        return null;
      }
    });
    client.requestAccessToken();
  }

  async generateSHA256(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  }
}
